import { apiQuery } from '../';

export const mcoOutreachCandidateApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getOutreachCandidates: builder.query({
        query: ({ page, params, searchTerm, sort, filter, limit = 20 }) => {
          const { outreachId } = params;

          if (searchTerm) {
            if (searchTerm?.length < 2) return;
            return {
              method: 'get',
              url: `/outreaches/${outreachId}/candidates`,
              params: {
                page: 1,
                items: 50,
                q: searchTerm,
              },
            };
          }

          const qParams = { page, items: limit, sort_by: {}, filter_by: {} };

          const { element = null, order = null } = sort;
          const {
            stages = [],
            tags = [],
            sourcedBy = [],
            step = [],
            haltReason = [],
          } = filter;
          let newStages = stages;
          if (element && order) {
            qParams['sort_by'][element] = order;
          }

          if (stages?.length) {
            qParams['filter_by']['stages'] = stages;
          }

          if (tags?.length) {
            const tagsId = tags?.map(({ id }) => id);
            qParams['filter_by']['candidate_tags'] = tagsId;
          }

          if (sourcedBy?.length) {
            qParams['filter_by']['sourced_by'] = sourcedBy;
          }

          if (step?.length) {
            const stepIds = step.map(({ id }) => id);
            qParams['filter_by']['step_id'] = stepIds;
          } else {
            qParams['filter_by']['step_id'] = [];
          }

          if (haltReason?.length) {
            // Since currently for flow restart all candidates will be in paused stage and halt reason will be added, But even though candidate is moved to stopped, halt reason will not clear away, So always including paused stage if flow restart filters applied
            qParams['filter_by']['halt_reason'] = haltReason;
            if (!stages?.includes('outreach_paused')) {
              newStages = [...newStages, 'outreach_paused'];
            }
            if (!stages?.includes('not_started')) {
              newStages = [...newStages, 'not_started'];
            }
            qParams['filter_by']['stages'] = newStages;
          } else {
            qParams['filter_by']['halt_reason'] = [];
            if (!stages?.includes('outreach_paused'))
              qParams['filter_by']['stages'] = stages?.filter(
                (stage) => stage !== 'outreach_paused'
              );
          }

          return {
            method: 'get',
            url: `/outreaches/${outreachId}/candidates`,
            params: qParams,
          };
        },
        providesTags: ['GetOutreachCandidates'],
      }),

      addCandidate: builder.mutation({
        query: ({ outreachId, contacts, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates`,
            method: 'post',
            data: {
              candidate_ids: contacts,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: ['GetContacts', 'GetOutreachCandidates'],
      }),

      moveStep: builder.mutation({
        query: ({ outreachId, candidates, step, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/step`,
            method: 'put',
            params: {
              candidate_ids: candidates,
              to_step_id: step,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: (result) => {
          if (result?.data) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      pauseCandidate: builder.mutation({
        query: ({ outreachId, candidates, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/pause`,
            method: 'put',
            data: {
              candidate_ids: candidates,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      resumeCandidate: builder.mutation({
        query: ({ outreachId, candidates, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/resume`,
            method: 'put',
            data: {
              candidate_ids: candidates,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      deleteCandidate: builder.mutation({
        query: ({ outreachId, candidates, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/bulk`,
            method: 'delete',
            data: {
              candidate_ids: candidates,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: (data) => {
          if (data) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      stopCandidate: builder.mutation({
        query: ({ outreachId, candidates, is_bulk_activity }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/stop`,
            method: 'put',
            data: {
              candidate_ids: candidates,
              ...(is_bulk_activity && { is_bulk_activity }),
            },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      restartOutreachCandidates: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ outreachId, candidates }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/restart_failed`,
            method: 'put',
            data: { candidates },
          };
        },
        invalidatesTags: (data) => {
          // Invalidate, if only all candidate is restarted.
          const canInvalidate = data?.every(
            (item) => item?.errors?.length === 0
          );
          if (canInvalidate) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      flowRestartDetails: builder.query({
        transformResponse: (response) => response.data,
        query: ({ params }) => {
          const { outreachId, candidateId } = params;
          return {
            method: 'get',
            url: `/outreaches/${outreachId}/candidates/${candidateId}`,
          };
        },
      }),

      skipStepForCandidate: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ outreachId, candidates }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/skip_failed`,
            method: 'put',
            data: { candidates: candidates },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      generateCSV: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ outreachId }) => {
          return {
            url: `/outreaches/${outreachId}/generate_csv`,
            method: 'post',
          };
        },
      }),
    };
  },
  overrideExisting: true,
});
export const {
  useGetOutreachCandidatesQuery,
  useAddCandidateMutation,
  useMoveStepMutation,
  usePauseCandidateMutation,
  useResumeCandidateMutation,
  useRestartOutreachCandidatesMutation,
  useStopCandidateMutation,
  useDeleteCandidateMutation,
  useFlowRestartDetailsQuery,
  useSkipStepForCandidateMutation,
  useGenerateCSVMutation,
} = mcoOutreachCandidateApi;
